/**
 * BiblePlanHealthDialog — reading-integrity health check + granular reset
 * tools for a scoped Bible plan. Used by ScopedBiblePlans (schools, churches,
 * events). Both tenant admins and super admins can run it.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Stethoscope, Flame, RotateCcw, History, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
};

const FLAG_LABELS = {
  streak_exceeds_days_enrolled: 'Streak > days enrolled',
  streak_exceeds_reading_dates: 'Streak > actual reading dates',
  completed_days_exceed_duration: 'Too many days ticked',
  current_day_out_of_range: 'Day counter out of range',
  future_last_completed: 'Future-dated reading',
};

export default function BiblePlanHealthDialog({ plan, scopeType, scopeId, onClose, onReset }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(new Set());
  const [mode, setMode] = useState('streak_only');
  const [reason, setReason] = useState('');
  const [showAudit, setShowAudit] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['plan-integrity', plan.id],
    queryFn: () => api(`/api/admin/bible/scope/plans/${plan.id}/integrity`),
  });

  const { data: audit } = useQuery({
    queryKey: ['reset-audit', scopeType, scopeId, plan.id],
    queryFn: () => api(`/api/admin/bible/scope/${scopeType}/${scopeId}/reset-audit?plan_id=${plan.id}`),
    enabled: showAudit,
  });

  const resetMutation = useMutation({
    mutationFn: (payload) => api(`/api/admin/bible/scope/plans/${plan.id}/reset-stats`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
    onSuccess: (res) => {
      toast.success(`Reset ${res.reset} reader(s) · mode: ${res.mode === 'streak_only' ? 'streak only' : 'full'}`);
      setSelected(new Set());
      setReason('');
      qc.invalidateQueries({ queryKey: ['plan-integrity', plan.id] });
      qc.invalidateQueries({ queryKey: ['reset-audit', scopeType, scopeId, plan.id] });
      onReset?.();
    },
    onError: (e) => toast.error(e.message),
  });

  const rows = data?.rows || [];
  const flagged = rows.filter((r) => !r.healthy);
  const toggle = (uid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });

  const runReset = (userIds, resetAll = false) => {
    const modeLabel = mode === 'streak_only' ? 'STREAK ONLY (progress kept)' : 'FULL RESET (progress wiped)';
    const who = resetAll ? `ALL ${rows.length} readers` : `${userIds.length} selected reader(s)`;
    if (!window.confirm(`${modeLabel}\n\nReset ${who} on "${plan.title}"?\nThis is recorded in the reset audit trail.`)) return;
    resetMutation.mutate({
      user_ids: resetAll ? null : userIds,
      mode,
      reason: reason || (mode === 'streak_only' ? 'Streak glitch fix' : 'Full stats reset'),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bible-plan-health-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="h-4 w-4 text-teal-600" /> Reading Health Check · {plan.title}
          </DialogTitle>
          <DialogDescription>
            Detects glitched streaks and lets you reset them — every reset is logged.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="text-sm text-slate-500 py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Scanning readers…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded bg-slate-50"><div className="text-xl font-bold">{data.summary.readers}</div><div className="text-xs text-slate-500">Readers</div></div>
              <div className="p-2 rounded bg-emerald-50"><div className="text-xl font-bold text-emerald-700">{data.summary.healthy}</div><div className="text-xs text-slate-500">Healthy</div></div>
              <div className={`p-2 rounded ${data.summary.flagged ? 'bg-rose-50' : 'bg-slate-50'}`} data-testid="health-flagged-count">
                <div className={`text-xl font-bold ${data.summary.flagged ? 'text-rose-700' : ''}`}>{data.summary.flagged}</div>
                <div className="text-xs text-slate-500">Flagged</div>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-2 border rounded-lg p-3 bg-slate-50/60">
              <div className="min-w-0 sm:min-w-[160px]">
                <Label className="text-xs">Reset mode</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="h-8 bg-white" data-testid="health-reset-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="streak_only">Streak only (keep progress)</SelectItem>
                    <SelectItem value="full">Full reset (wipe progress)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-0 sm:min-w-[180px]">
                <Label className="text-xs">Reason (audit trail)</Label>
                <Input className="h-8 bg-white" value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. glitched streak numbers" data-testid="health-reset-reason" />
              </div>
              <Button size="sm" variant="outline" disabled={selected.size === 0 || resetMutation.isPending}
                onClick={() => runReset(Array.from(selected))} data-testid="health-reset-selected-btn">
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset selected ({selected.size})
              </Button>
              <Button size="sm" variant="destructive" disabled={rows.length === 0 || resetMutation.isPending}
                onClick={() => runReset([], true)} data-testid="health-reset-all-btn">
                {resetMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
                Reset everyone
              </Button>
            </div>

            <div className="max-h-[40vh] overflow-y-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 text-left">Reader</th>
                    <th className="p-2 text-left">Streak</th>
                    <th className="p-2 text-left">Reading dates</th>
                    <th className="p-2 text-left">Days done</th>
                    <th className="p-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={6} className="p-4 text-center text-slate-400">No readers on this plan yet.</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.user_plan_id} className={`border-t ${!r.healthy ? 'bg-rose-50/50' : ''}`} data-testid={`health-row-${r.user_id}`}>
                      <td className="p-2"><Checkbox checked={selected.has(r.user_id)} onCheckedChange={() => toggle(r.user_id)} data-testid={`health-check-${r.user_id}`} /></td>
                      <td className="p-2"><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500">{r.email}</div></td>
                      <td className="p-2"><span className="flex items-center gap-1"><Flame className="h-3 w-3 text-amber-500" /> {r.streak}</span></td>
                      <td className="p-2 text-xs">{r.distinct_reading_dates}{r.days_enrolled ? ` / ${r.days_enrolled}d enrolled` : ''}</td>
                      <td className="p-2 text-xs">{r.completed_days_count}/{r.duration_days}</td>
                      <td className="p-2">
                        {r.healthy ? (
                          <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-0.5" />OK</Badge>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {r.flags.map((f) => (
                              <Badge key={f} variant="outline" className="text-[10px] text-rose-700 border-rose-300 bg-rose-50">
                                <ShieldAlert className="h-3 w-3 mr-0.5" />{FLAG_LABELS[f] || f}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {flagged.length > 0 && (
              <Button size="sm" variant="outline" className="text-rose-700 border-rose-200"
                onClick={() => setSelected(new Set(flagged.map((r) => r.user_id)))} data-testid="health-select-flagged-btn">
                Select all {flagged.length} flagged reader(s)
              </Button>
            )}

            <div>
              <button className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
                onClick={() => setShowAudit((v) => !v)} data-testid="health-audit-toggle">
                <History className="h-3.5 w-3.5" /> {showAudit ? 'Hide' : 'Show'} reset history
              </button>
              {showAudit && (
                <div className="mt-2 border rounded max-h-40 overflow-y-auto">
                  {!audit ? (
                    <div className="p-3 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Loading…</div>
                  ) : (audit.entries || []).length === 0 ? (
                    <div className="p-3 text-xs text-slate-400">No resets recorded for this plan yet.</div>
                  ) : (
                    <ul className="divide-y text-xs" data-testid="health-audit-list">
                      {audit.entries.map((e) => (
                        <li key={e.id} className="px-3 py-2 flex items-center justify-between gap-2">
                          <div>
                            <span className="font-medium">{e.actor_email || 'admin'}</span> reset {e.affected} reader(s)
                            · <Badge variant="outline" className="text-[10px]">{e.mode}</Badge>
                            {e.reason && <span className="text-slate-500"> · “{e.reason}”</span>}
                          </div>
                          <span className="text-slate-400 shrink-0">{e.at ? new Date(e.at).toLocaleString() : ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="health-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
